import { getCurrentToken } from '@qse/api'
import {
  AppRoutesList,
  CheckoutCompletedPayload,
  CheckoutFailedPayload,
  UserRoleEnum,
  UserSuspendedPayload,
} from '@qse/constants'
import { useRealtime } from '@qse/realtime'
import { useBillingStatusStore, useUserStore } from '@qse/stores'
import { toast } from '@qse/ui'
import { detectCurrentApp, forceLogout } from '@qse/utils'
import { useEffect } from 'react'

import { useRouteContext } from './use-route-context'

// Extend the existing payload types for payment events
interface PaymentFailurePayload {
  tenantId: string
  subscriptionId: string
  failureReason: string
  gracePeriodStart: string
  gracePeriodEnd: string
  retryAttempts: number
  nextRetryDate?: string
}

interface PaymentResolvedPayload {
  tenantId: string
  subscriptionId: string
  resolvedAt: string
  newBillingStatus: 'active' | 'past_due' | 'cancelled'
}

interface GracePeriodExpiredPayload {
  tenantId: string
  subscriptionId: string
  expiredAt: string
  finalStatus: 'cancelled' | 'suspended'
}

/**
 * Custom real-time hook for admin app with admin-specific event handlers
 */
export function useAdminRealtime() {
  const routeContext = useRouteContext()
  const { user } = useUserStore()
  const {
    setBillingStatus,
    setGracePeriodStatus,
    addPaymentFailureNotification,
    setShowPaymentOverdueBanner,
  } = useBillingStatusStore()

  const realtime = useRealtime({
    getToken: getCurrentToken,
    shouldConnect: () => detectCurrentApp().isAdmin && user?.userRole === UserRoleEnum.TENANT_ADMIN,
    getRouteContext: () => routeContext,
    eventHandlers: {
      // Admin-specific event handlers
      onUserActivity: (payload: any) => {
        console.log('User activity detected:', payload)
        // Could trigger user list refresh or notifications
      },

      onSystemStatusUpdate: (payload: any) => {
        console.log('System status updated:', payload)
        toast.info('System status has been updated')
      },

      onUserStorageQuotaUpdated: (payload: any) => {
        console.log('User storage quota updated:', payload)
        // Could trigger user list refresh or show notifications
        toast.info('User storage quota has been updated')
      },

      onUserServiceUsageUpdated: (payload: any) => {
        console.log('User service usage updated:', payload)
        // Could trigger user list refresh or show notifications
        toast.info('User service usage has been updated')
      },

      // Handle file events for admin monitoring
      onFileEncryptionCompleted: (payload: any) => {
        console.log('File encryption completed:', payload)
        // Could show admin notifications for large file uploads
        if (payload.file?.sizeBytes && payload.file.sizeBytes > 100 * 1024 * 1024) {
          // 100MB
          toast.info(`Large file uploaded: ${payload.file.name}`)
        }
      },

      onFileEncryptionFailed: (payload: any) => {
        console.log('File encryption failed:', payload)
        toast.error(`File encryption failed: ${payload.file?.name}`)
      },

      onFileCopied: (payload: any) => {
        console.log('File copied:', payload)
        // Could track file operations for admin monitoring
      },

      onFileReadyForDownload: (payload: any) => {
        console.log('File ready for download:', payload)
        // Could track download patterns
      },

      onFileDownloadRequestFailed: (payload: any) => {
        console.log('File download request failed:', payload)
        toast.error('File download request failed')
      },

      onFileCopyFailed: (payload: any) => {
        console.log('File copy failed:', payload)
        toast.error('File copy operation failed')
      },

      // Handle checkout events
      onCheckoutFailed: (payload: CheckoutFailedPayload) => {
        console.log('Checkout failed:', payload)
        const reason = payload.reason || 'Unknown error'

        window.location.href = `/billing/${payload.planId}/error`
        toast.error(`Payment failed: ${reason}`)
      },

      onCheckoutCompleted: (payload: CheckoutCompletedPayload) => {
        console.log('Checkout completed:', payload)
        window.location.href = AppRoutesList.users
        toast.success('Payment completed successfully!')
      },

      onUserSuspended: (payload: UserSuspendedPayload) => {
        console.log('🚫 Admin user suspended:', payload)
        forceLogout(true)
      },

      // New payment failure event handlers
      onPaymentFailure: (payload: PaymentFailurePayload) => {
        console.log('💳 Payment failure detected:', payload)

        // Update billing status to past_due
        setBillingStatus('past_due')

        // Set grace period status
        setGracePeriodStatus({
          isInGracePeriod: true,
          gracePeriodStart: payload.gracePeriodStart,
          gracePeriodEnd: payload.gracePeriodEnd,
          daysRemaining: Math.ceil(
            (new Date(payload.gracePeriodEnd).getTime() - new Date().getTime()) /
              (1000 * 60 * 60 * 24),
          ),
          paymentFailureReason: payload.failureReason,
          retryAttempts: payload.retryAttempts,
          nextRetryDate: payload.nextRetryDate,
        })

        // Add payment failure notification
        addPaymentFailureNotification({
          id: `payment-failure-${Date.now()}`,
          tenantId: payload.tenantId,
          subscriptionId: payload.subscriptionId,
          failureReason: payload.failureReason,
          failureDate: new Date().toISOString(),
          gracePeriodStart: payload.gracePeriodStart,
          gracePeriodEnd: payload.gracePeriodEnd,
          isActive: true,
          retryAttempts: payload.retryAttempts,
          nextRetryDate: payload.nextRetryDate,
        })

        // Show payment overdue banner
        setShowPaymentOverdueBanner(true)

        // Show notification to user
        toast.error(`Payment failed: ${payload.failureReason}`, {
          description: 'Your account is now in a grace period. Please update your payment method.',
          duration: 10000,
        })
      },

      onPaymentResolved: (payload: PaymentResolvedPayload) => {
        console.log('✅ Payment resolved:', payload)

        // Update billing status
        setBillingStatus(payload.newBillingStatus)

        // Clear grace period if payment is now active
        if (payload.newBillingStatus === 'active') {
          setGracePeriodStatus(null)
          setShowPaymentOverdueBanner(false)

          toast.success('Payment resolved successfully!', {
            description: 'Your subscription is now active.',
          })
        }
      },

      onGracePeriodExpired: (payload: GracePeriodExpiredPayload) => {
        console.log('⏰ Grace period expired:', payload)

        // Update billing status to cancelled
        setBillingStatus('cancelled')

        // Clear grace period
        setGracePeriodStatus(null)

        // Show critical notification
        toast.error('Grace period expired', {
          description:
            'Your account has been suspended due to payment issues. Please contact support immediately.',
          duration: 15000,
        })

        // Redirect to billing page
        setTimeout(() => {
          window.location.href = AppRoutesList.billing
        }, 2000)
      },

      onSubscriptionRetryAttempt: (payload: PaymentFailurePayload) => {
        console.log('🔄 Subscription retry attempt:', payload)

        // Update grace period status with new retry information
        setGracePeriodStatus({
          isInGracePeriod: true,
          gracePeriodStart: payload.gracePeriodStart,
          gracePeriodEnd: payload.gracePeriodEnd,
          daysRemaining: Math.ceil(
            (new Date(payload.gracePeriodEnd).getTime() - new Date().getTime()) /
              (1000 * 60 * 60 * 24),
          ),
          paymentFailureReason: payload.failureReason,
          retryAttempts: payload.retryAttempts,
          nextRetryDate: payload.nextRetryDate,
        })

        toast.info('Payment retry attempted', {
          description: `Retry attempt ${payload.retryAttempts} completed. Next retry: ${payload.nextRetryDate ? new Date(payload.nextRetryDate).toLocaleDateString() : 'TBD'}`,
        })
      },

      // Billing state change events
      onBillingPastDue: (_payload: any) => {
        setBillingStatus('past_due')
        setShowPaymentOverdueBanner(true)
      },

      onBillingUnpaid: (_payload: any) => {
        forceLogout(true)
      },

      onBillingCanceled: (_payload: any) => {
        forceLogout(true)
      },
    },
  })

  // Log connection state changes for debugging
  useEffect(() => {
    const { isConnected, isConnecting, error } = realtime.connectionState

    if (isConnected) {
      console.log('✅ Admin WebSocket connected successfully')
    } else if (isConnecting) {
      console.log('🔄 Admin WebSocket connecting...')
    } else if (error) {
      console.warn('❌ Admin WebSocket error:', error)
    }
  }, [realtime.connectionState])

  return realtime
}
